import { useState, useEffect, useCallback } from 'react';
import { storePurchaseOrdersAPI, warehousesAPI, storesAPI, inventoryAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, RefreshCw, ShoppingCart, CheckCircle, XCircle, 
  Eye, Store, Clock, Package, Warehouse, FileCheck, Building2,
  AlertTriangle, AlertCircle
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const FranchisePOApprovals = () => {
  const { toast } = useToast();
  
  const [pendingPOs, setPendingPOs] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [ownedStores, setOwnedStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Approval dialog
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [sourceType, setSourceType] = useState('warehouse'); // 'warehouse' or 'store'
  const [approvalForm, setApprovalForm] = useState({
    source_warehouse_id: '',
    source_store_id: '',
    review_notes: '',
    items: []
  });
  const [processing, setProcessing] = useState(false);
  
  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  
  // Stock availability
  const [stockAvailability, setStockAvailability] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);

  const fetchPendingPOs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 100 };
      if (search) params.search = search;
      
      const response = await storePurchaseOrdersAPI.hqPendingApprovals(params);
      setPendingPOs(response.data.items || []);
    } catch (error) {
      console.error('Error fetching pending POs:', error);
      toast({ title: 'Error', description: 'Failed to fetch pending approvals', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, toast]);

  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await warehousesAPI.list({ page_size: 100 });
      setWarehouses(response.data.items || response.data || []);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  }, []);

  const fetchOwnedStores = useCallback(async () => {
    try {
      const response = await storesAPI.list({ store_type: 'owned', page_size: 100 });
      setOwnedStores(response.data.items || response.data || []);
    } catch (error) {
      console.error('Error fetching owned stores:', error);
    }
  }, []);

  useEffect(() => {
    fetchPendingPOs();
    fetchWarehouses();
    fetchOwnedStores();
  }, [fetchPendingPOs, fetchWarehouses, fetchOwnedStores]);

  // Fetch stock availability when source is selected
  const fetchStockAvailability = useCallback(async (srcType, srcId, productIds) => {
    if (!srcId || !productIds.length) {
      setStockAvailability(null);
      return;
    }
    
    setLoadingStock(true);
    try {
      const response = await inventoryAPI.checkStockAvailability({
        source_type: srcType,
        source_id: srcId,
        product_ids: productIds
      });
      setStockAvailability(response.data);
    } catch (error) {
      console.error('Error fetching stock availability:', error);
      setStockAvailability(null);
    } finally {
      setLoadingStock(false);
    }
  }, []);

  // Trigger stock check when source changes
  useEffect(() => {
    if (!selectedPO) return;
    
    const sourceId = sourceType === 'warehouse' ? approvalForm.source_warehouse_id : approvalForm.source_store_id;
    const productIds = selectedPO.items.map(item => item.product_id);
    
    if (sourceId) {
      fetchStockAvailability(sourceType, sourceId, productIds);
    } else {
      setStockAvailability(null);
    }
  }, [sourceType, approvalForm.source_warehouse_id, approvalForm.source_store_id, selectedPO, fetchStockAvailability]);

  const openApprovalDialog = (po) => {
    setSelectedPO(po);
    setSourceType('warehouse');
    setApprovalForm({
      source_warehouse_id: '',
      source_store_id: '',
      review_notes: '',
      items: po.items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        ordered_qty: item.ordered_qty,
        approved_qty: item.ordered_qty // Default to full approval
      }))
    });
    setApprovalDialogOpen(true);
  };

  const openRejectDialog = (po) => {
    setSelectedPO(po);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleApprove = async () => {
    // Validate source selection based on type
    if (sourceType === 'warehouse' && !approvalForm.source_warehouse_id) {
      toast({ title: 'Error', description: 'Please select a source warehouse', variant: 'destructive' });
      return;
    }
    if (sourceType === 'store' && !approvalForm.source_store_id) {
      toast({ title: 'Error', description: 'Please select a source store', variant: 'destructive' });
      return;
    }

    setProcessing(true);
    try {
      const data = {
        source_type: sourceType,
        source_warehouse_id: sourceType === 'warehouse' ? approvalForm.source_warehouse_id : null,
        source_store_id: sourceType === 'store' ? approvalForm.source_store_id : null,
        review_notes: approvalForm.review_notes,
        items: approvalForm.items.map(item => ({
          product_id: item.product_id,
          approved_qty: item.approved_qty
        }))
      };
      
      const response = await storePurchaseOrdersAPI.hqApprove(selectedPO.id, data);
      toast({ 
        title: 'Success', 
        description: `PO approved. Transfer Order ${response.data.transfer_order?.transfer_number || ''} created.` 
      });
      setApprovalDialogOpen(false);
      fetchPendingPOs();
    } catch (error) {
      console.error('Error approving PO:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to approve purchase order', 
        variant: 'destructive' 
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast({ title: 'Error', description: 'Please provide a rejection reason', variant: 'destructive' });
      return;
    }

    setProcessing(true);
    try {
      await storePurchaseOrdersAPI.hqReject(selectedPO.id, rejectReason);
      toast({ title: 'Success', description: 'Purchase Order rejected' });
      setRejectDialogOpen(false);
      fetchPendingPOs();
    } catch (error) {
      console.error('Error rejecting PO:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to reject purchase order', 
        variant: 'destructive' 
      });
    } finally {
      setProcessing(false);
    }
  };

  const updateApprovedQty = (productId, qty) => {
    setApprovalForm(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.product_id === productId 
          ? { ...item, approved_qty: Math.min(parseFloat(qty) || 0, item.ordered_qty) }
          : item
      )
    }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Get stock status for a product
  const getStockStatus = (productId) => {
    if (!stockAvailability) return null;
    return stockAvailability.items?.find(item => item.product_id === productId);
  };

  // Check if there are any stock issues
  const hasStockIssues = () => {
    if (!stockAvailability) return false;
    return stockAvailability.items?.some(item => {
      const approvalItem = approvalForm.items.find(a => a.product_id === item.product_id);
      return approvalItem && item.available_qty < approvalItem.approved_qty;
    });
  };

  return (
    <div className="p-6 space-y-6" data-testid="franchise-po-approvals-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileCheck className="w-6 h-6 text-blue-600" />
            Franchise PO Approvals
          </h1>
          <p className="text-gray-500 mt-1">
            Review and approve stock requests from franchise stores
          </p>
        </div>
        <Button onClick={fetchPendingPOs} variant="outline">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Approvals</p>
                <p className="text-2xl font-bold text-yellow-600">{pendingPOs.length}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Value</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(pendingPOs.reduce((sum, po) => sum + po.grand_total, 0))}
                </p>
              </div>
              <ShoppingCart className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Unique Stores</p>
                <p className="text-2xl font-bold text-purple-600">
                  {new Set(pendingPOs.map(po => po.store_id)).size}
                </p>
              </div>
              <Store className="w-8 h-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by PO number or store..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Pending POs Table */}
      <Card>
        <CardHeader className="py-3 bg-gray-50/50 border-b">
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5 text-gray-500" />
            Pending Purchase Orders
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>PO Number</TableHead>
                <TableHead>Store</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="text-right">Total Value</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    Loading pending approvals...
                  </TableCell>
                </TableRow>
              ) : pendingPOs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No pending approvals
                  </TableCell>
                </TableRow>
              ) : (
                pendingPOs.map((po) => (
                  <TableRow key={po.id} data-testid={`pending-po-${po.id}`}>
                    <TableCell className="font-mono text-sm font-medium">{po.po_number}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{po.store_name}</p>
                        <p className="text-xs text-gray-500">{po.store_code}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{po.items.length} items</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(po.submitted_at)}</TableCell>
                    <TableCell className="text-right font-medium text-blue-600">
                      {formatCurrency(po.grand_total)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => openApprovalDialog(po)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => openRejectDialog(po)}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="bg-white max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Approve Purchase Order - {selectedPO?.po_number}
            </DialogTitle>
            <DialogDescription>
              Review items and assign source warehouse for {selectedPO?.store_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedPO && (
            <div className="space-y-4">
              {/* Store Info */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-blue-600">Store</p>
                    <p className="font-medium">{selectedPO.store_name}</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Code</p>
                    <p className="font-medium">{selectedPO.store_code}</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Total Value</p>
                    <p className="font-medium text-lg">{formatCurrency(selectedPO.grand_total)}</p>
                  </div>
                </div>
              </div>

              {/* Source Selection */}
              <div className="space-y-4">
                <Label className="text-base font-medium">Select Source *</Label>
                <p className="text-sm text-gray-500">Choose where to fulfill this order from</p>
                
                {/* Source Type Selection */}
                <RadioGroup 
                  value={sourceType} 
                  onValueChange={(val) => {
                    setSourceType(val);
                    setApprovalForm(prev => ({ ...prev, source_warehouse_id: '', source_store_id: '' }));
                  }}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="warehouse" id="source-warehouse" />
                    <Label htmlFor="source-warehouse" className="flex items-center gap-2 cursor-pointer">
                      <Warehouse className="w-4 h-4 text-blue-600" />
                      Warehouse
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="store" id="source-store" />
                    <Label htmlFor="source-store" className="flex items-center gap-2 cursor-pointer">
                      <Store className="w-4 h-4 text-green-600" />
                      Owned Store
                    </Label>
                  </div>
                </RadioGroup>
                
                {/* Warehouse Selection */}
                {sourceType === 'warehouse' && (
                  <Select 
                    value={approvalForm.source_warehouse_id} 
                    onValueChange={(val) => setApprovalForm(prev => ({ ...prev, source_warehouse_id: val }))}
                  >
                    <SelectTrigger className="w-full md:w-1/2">
                      <SelectValue placeholder="Select warehouse..." />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouses.map(wh => (
                        <SelectItem key={wh.id} value={wh.id}>
                          <div className="flex items-center gap-2">
                            <Warehouse className="w-4 h-4 text-blue-500" />
                            {wh.name} ({wh.code})
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                
                {/* Owned Store Selection */}
                {sourceType === 'store' && (
                  <Select 
                    value={approvalForm.source_store_id} 
                    onValueChange={(val) => setApprovalForm(prev => ({ ...prev, source_store_id: val }))}
                  >
                    <SelectTrigger className="w-full md:w-1/2">
                      <SelectValue placeholder="Select owned store..." />
                    </SelectTrigger>
                    <SelectContent>
                      {ownedStores.filter(s => s.id !== selectedPO?.store_id).map(store => (
                        <SelectItem key={store.id} value={store.id}>
                          <div className="flex items-center gap-2">
                            <Store className="w-4 h-4 text-green-500" />
                            {store.name} ({store.code})
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Stock Availability Summary */}
              {stockAvailability && (
                <div className={`p-3 rounded-lg ${hasStockIssues() ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                  <div className="flex items-center gap-2">
                    {hasStockIssues() ? (
                      <>
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <span className="text-red-700 font-medium">Stock shortage detected at {stockAvailability.source_name}</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-green-700 font-medium">All items available at {stockAvailability.source_name}</span>
                      </>
                    )}
                  </div>
                </div>
              )}
              
              {loadingStock && (
                <div className="flex items-center gap-2 text-gray-500">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Checking stock availability...</span>
                </div>
              )}

              {/* Items Table */}
              <div className="space-y-2">
                <Label className="text-base font-medium">Approve Quantities</Label>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Product</TableHead>
                        <TableHead className="text-right">Requested</TableHead>
                        <TableHead className="text-center">Available Stock</TableHead>
                        <TableHead className="w-[150px]">Approve Qty</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Line Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {approvalForm.items.map((item, idx) => {
                        const lineTotal = item.approved_qty * (selectedPO.items[idx]?.unit_price || 0);
                        const stockStatus = getStockStatus(item.product_id);
                        const availableQty = stockStatus?.available_qty ?? null;
                        const hasShortage = availableQty !== null && availableQty < item.approved_qty;
                        const isLowStock = stockStatus?.low_stock;
                        const isOutOfStock = availableQty === 0;
                        
                        return (
                          <TableRow key={item.product_id} className={hasShortage ? 'bg-red-50' : ''}>
                            <TableCell>
                              <p className="font-medium text-sm">{item.product_name}</p>
                            </TableCell>
                            <TableCell className="text-right">{item.ordered_qty}</TableCell>
                            <TableCell className="text-center">
                              {stockAvailability ? (
                                <div className="flex items-center justify-center gap-1">
                                  {isOutOfStock ? (
                                    <Badge variant="destructive" className="gap-1">
                                      <AlertCircle className="w-3 h-3" />
                                      Out of Stock
                                    </Badge>
                                  ) : isLowStock ? (
                                    <Badge variant="outline" className="border-amber-500 text-amber-600 gap-1">
                                      <AlertTriangle className="w-3 h-3" />
                                      {availableQty}
                                    </Badge>
                                  ) : hasShortage ? (
                                    <Badge variant="destructive" className="gap-1">
                                      <AlertTriangle className="w-3 h-3" />
                                      {availableQty}
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="border-green-500 text-green-600">
                                      {availableQty}
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">Select source</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                max={item.ordered_qty}
                                value={item.approved_qty}
                                onChange={(e) => updateApprovedQty(item.product_id, e.target.value)}
                                className={`h-8 ${hasShortage ? 'border-red-400' : ''}`}
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(selectedPO.items[idx]?.unit_price || 0)}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(lineTotal)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Approval Total */}
              <div className="flex justify-end">
                <div className="w-64 bg-green-50 p-3 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-green-700">Approved Total</span>
                    <span className="font-bold text-green-700">
                      {formatCurrency(
                        approvalForm.items.reduce((sum, item, idx) => 
                          sum + (item.approved_qty * (selectedPO.items[idx]?.unit_price || 0)), 0
                        )
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Review Notes</Label>
                <Textarea
                  value={approvalForm.review_notes}
                  onChange={(e) => setApprovalForm(prev => ({ ...prev, review_notes: e.target.value }))}
                  placeholder="Optional notes for the store..."
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleApprove}
              disabled={processing}
              className="bg-green-600 hover:bg-green-700"
            >
              {processing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve & Create Transfer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Reject Purchase Order
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting {selectedPO?.po_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg text-sm">
              <p><span className="text-gray-500">Store:</span> {selectedPO?.store_name}</p>
              <p><span className="text-gray-500">Value:</span> {formatCurrency(selectedPO?.grand_total)}</p>
            </div>
            
            <div className="space-y-2">
              <Label>Rejection Reason *</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleReject}
              disabled={processing}
            >
              {processing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject PO
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FranchisePOApprovals;
